import { getStripeLookupKey } from './checkout';
import type { Plan } from '../content/plans';
import type { PlanId } from './checkout';
import { getCurrencyForLocale, getCurrencySymbol } from './currencies';

interface StripePriceData {
  unit_amount: number;
  currency: string;
  product?: string; // Add product ID
}

// Get the API URL from environment variables
const API_URL = import.meta.env.VITE_WORKER_URL || '';

export async function fetchStripePrices(locale: string = 'en', eventCode?: string, referralCode?: string): Promise<{
  prices: Record<PlanId, StripePriceData>,
  trialEligibility: Record<PlanId, { trialEligible: boolean; trialPeriodDays?: number }>,
  promo?: {
    valid: boolean;
    applied: boolean;
    originalPrices?: Record<string, StripePriceData>;
    eventOnlyPrices?: Record<string, StripePriceData>;
    hasReferral?: boolean;
  }
}> {
  const quarterlyKey = getStripeLookupKey('quarterly');
  const annualKey = getStripeLookupKey('annual');
  const targetCurrency = getCurrencyForLocale(locale);

  const response = await fetch(`${API_URL}/api/prices`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      lookupKeys: [quarterlyKey, annualKey],
      currency: targetCurrency,
      eventCode: eventCode || undefined,
      referralCode: referralCode || undefined,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch Stripe prices');
  }

  const result = await response.json();

  // Extract promo info and trial eligibility, remove them from the main object
  const { promo, trialEligibility: trialEligibilityByLookupKey, ...prices } = result;

  // Map trial eligibility from lookup keys to PlanId
  const trialEligibility: Record<PlanId, { trialEligible: boolean; trialPeriodDays?: number }> = {
    quarterly: trialEligibilityByLookupKey?.[quarterlyKey] || { trialEligible: false },
    annual: trialEligibilityByLookupKey?.[annualKey] || { trialEligible: false },
  };

  return {
    prices: {
      quarterly: prices[quarterlyKey],
      annual: prices[annualKey],
    },
    trialEligibility,
    promo
  };
}

export function calculateAnnualDiscount(quarterlyPrice: number, annualPrice: number): number {
  const annualEquivalent = quarterlyPrice * 4;
  return Math.round(((annualEquivalent - annualPrice) / annualEquivalent) * 100);
}


export async function getUpdatedPlans(plans: Plan[], locale: string = 'en', eventCode?: string, referralCode?: string): Promise<Plan[]> {
  const result = await fetchStripePrices(locale, eventCode, referralCode);
  const { prices, trialEligibility, promo } = result;
  const currencySymbol = getCurrencySymbol(locale);

  console.log('prices', prices, 'promo', promo);

  return plans.map(plan => {
    const price = prices[plan.id as PlanId];
    console.log('price', price, "plan", plan);
    if (!price) return plan;

    const updatedPlan: Plan = JSON.parse(JSON.stringify(plan));
    updatedPlan.pricing.currency = currencySymbol;

    // The price is already the final price (promo applied via lookup key)
    const finalPrice = price.unit_amount;
    updatedPlan.pricing.current = finalPrice;

    // Set trial eligibility from API response
    const trialData = trialEligibility[plan.id as PlanId];
    if (trialData) {
      updatedPlan.pricing.trialEligible = trialData.trialEligible;
      updatedPlan.pricing.trialPeriodDays = trialData.trialPeriodDays;
    }

    // Promo percent: total discount from original to final (both event + referral)
    if (promo && promo.applied && promo.originalPrices) {
      const planLookupKey = getStripeLookupKey(plan.id as PlanId);
      const originalPrice = promo.originalPrices[planLookupKey];
      if (originalPrice && originalPrice.unit_amount > finalPrice) {
        const percentSavings = Math.round(((originalPrice.unit_amount - finalPrice) / originalPrice.unit_amount) * 100);
        (updatedPlan.pricing as any).promoPercent = percentSavings;
        (updatedPlan.pricing as any).originalPrice = originalPrice.unit_amount;
      }
    }

    // Referral savings only: price before referral − final. If both event+referral, "before referral" = event-only price.
    if (promo?.hasReferral) {
      const planLookupKey = getStripeLookupKey(plan.id as PlanId);
      const priceBeforeReferral = promo.eventOnlyPrices?.[planLookupKey]?.unit_amount ?? promo.originalPrices?.[planLookupKey]?.unit_amount;
      if (priceBeforeReferral != null && priceBeforeReferral > finalPrice) {
        (updatedPlan.pricing as any).referralSavings = priceBeforeReferral - finalPrice;
      } else {
        (updatedPlan.pricing as any).referralSavings = 0;
      }
    }

    // Calculate original price and discount for annual plan based on quarterly price
    if (plan.id === 'annual' && prices.quarterly) {
      const quarterlyBasePrice = prices.quarterly.unit_amount;
      updatedPlan.pricing.original = quarterlyBasePrice * 4;

      // Calculate discount percentage using final price vs quarterly equivalent
      const discountPercentage = calculateAnnualDiscount(quarterlyBasePrice, finalPrice);

      if (updatedPlan.pricing.discount) {
        updatedPlan.pricing.discount.percentage = discountPercentage;
      }
    }

    return updatedPlan;
  });
}
